import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

function BuildForMeDropdown({ onClose }: { onClose: () => void }) {
  const [view, setView] = useState<'options' | 'form'>('options');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const data = new URLSearchParams();
    new FormData(e.currentTarget).forEach((v, k) => data.append(k, v.toString()));
    try {
      const res = await fetch('https://readdy.ai/api/form/d7e79hr5bqmofchk7aqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) setSubmitted(true);
    } catch (err) {
      console.error(err);
    }
    setSubmitting(false);
  };

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-2 w-80 bg-white border border-black/10 rounded-xl overflow-hidden z-50"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {submitted ? (
        <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-black mb-3">
            <i className="ri-check-line text-white text-lg"></i>
          </div>
          <p className="text-sm font-bold text-black mb-1">Request Sent!</p>
          <p className="text-xs text-black/40">We'll call you shortly.</p>
          <button onClick={onClose} className="mt-4 text-xs text-black/40 underline cursor-pointer">Close</button>
        </div>
      ) : view === 'options' ? (
        <div className="p-4 flex flex-col gap-2">
          <p className="text-xs font-bold text-black/40 uppercase tracking-widest mb-1">How would you like to connect?</p>

          {/* Call option */}
          <a
            href="tel:+19512909947"
            className="flex items-center gap-3 p-3 border border-black/10 rounded-xl hover:bg-black hover:text-white transition-all cursor-pointer group"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-black/5 group-hover:bg-white/15 flex-shrink-0">
              <i className="ri-phone-line text-sm group-hover:text-white text-black"></i>
            </div>
            <div>
              <p className="text-sm font-semibold leading-tight">Call Us Now</p>
              <p className="text-xs text-black/40 group-hover:text-white/60">+1 (951) 290-9947</p>
            </div>
          </a>

          {/* Submit request option */}
          <button
            onClick={() => setView('form')}
            className="flex items-center gap-3 p-3 border border-black/10 rounded-xl hover:bg-black hover:text-white transition-all cursor-pointer group text-left w-full"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-black/5 group-hover:bg-white/15 flex-shrink-0">
              <i className="ri-file-text-line text-sm group-hover:text-white text-black"></i>
            </div>
            <div>
              <p className="text-sm font-semibold leading-tight">Submit a Request</p>
              <p className="text-xs text-black/40 group-hover:text-white/60">We'll reach out within 24 hrs</p>
            </div>
          </button>
        </div>
      ) : (
        <div className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <button onClick={() => setView('options')} className="text-black/40 hover:text-black cursor-pointer">
              <i className="ri-arrow-left-line text-sm"></i>
            </button>
            <p className="text-xs font-bold text-black/40 uppercase tracking-widest">Submit a Request</p>
          </div>

          <form data-readdy-form id="build-for-me-navbar-form" onSubmit={handleSubmit} className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-black/40 uppercase tracking-wider">First Name *</label>
                <input type="text" name="first_name" required placeholder="Jane" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm text-black placeholder-black/30 focus:outline-none focus:border-black transition-colors" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-black/40 uppercase tracking-wider">Last Name *</label>
                <input type="text" name="last_name" required placeholder="Smith" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm text-black placeholder-black/30 focus:outline-none focus:border-black transition-colors" />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-black/40 uppercase tracking-wider">Email *</label>
              <input type="email" name="email" required placeholder="jane@example.com" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm text-black placeholder-black/30 focus:outline-none focus:border-black transition-colors" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-black/40 uppercase tracking-wider">Phone *</label>
              <input type="tel" name="phone" required placeholder="+1 (555) 000-0000" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm text-black placeholder-black/30 focus:outline-none focus:border-black transition-colors" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-black/40 uppercase tracking-wider">Tell us about your project</label>
              <textarea
                name="message"
                placeholder="What kind of website do you need?"
                maxLength={500}
                rows={3}
                onChange={e => setCharCount(e.target.value.length)}
                className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm text-black placeholder-black/30 focus:outline-none focus:border-black transition-colors resize-none"
              />
              <p className="text-xs text-black/30 text-right">{charCount}/500</p>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-2.5 bg-black text-white text-sm font-semibold rounded-full hover:bg-black/80 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {submitting ? 'Sending...' : 'Send Request'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export default function BrutalNavbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [buildOpen, setBuildOpen] = useState(false);

  return (
    <header
      className="w-full border-b border-black/10 bg-white sticky top-0 z-50"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <div className="max-w-[1400px] mx-auto px-5 h-11 flex items-center justify-between">
        {/* Logo (left) */}
        <Link to="/brutal" className="flex items-center flex-shrink-0">
          <img
            src="https://storage.readdy-site.link/project_files/99e1b8a4-43ac-4e40-af62-6e1ff41b7af4/c2d60336-e1ee-48d2-941c-10c8b5d22972_669141699_3389302541232803_3328452515874521566_n.jpg?v=db813952b8e45e94fdc62cb746fea51b"
            alt="Nova AI Digital"
            className="h-8 w-auto object-contain"
          />
        </Link>

        {/* Desktop nav links */}
        <nav className="hidden md:flex items-center gap-5">
          <Link to="/brutal-about" className="text-sm text-black hover:opacity-50 transition-opacity underline underline-offset-2 decoration-black/30 whitespace-nowrap cursor-pointer">
            About
          </Link>
          <a href="https://www.instagram.com/novaaidigitalllc/" target="_blank" rel="noopener noreferrer" className="w-5 h-5 flex items-center justify-center text-black hover:opacity-50 transition-opacity cursor-pointer">
            <i className="ri-instagram-line text-base"></i>
          </a>
          <a href="https://www.facebook.com/profile.php?id=61580586208643" target="_blank" rel="noopener noreferrer" className="w-5 h-5 flex items-center justify-center text-black hover:opacity-50 transition-opacity cursor-pointer">
            <i className="ri-facebook-line text-base"></i>
          </a>
        </nav>

        {/* Mobile: hamburger + social */}
        <div className="md:hidden flex items-center gap-3">
          <a href="https://www.instagram.com/novaaidigitalllc/" target="_blank" rel="noopener noreferrer" className="w-7 h-7 flex items-center justify-center text-black hover:opacity-50 transition-opacity cursor-pointer">
            <i className="ri-instagram-line text-lg"></i>
          </a>
          <a href="https://www.facebook.com/profile.php?id=61580586208643" target="_blank" rel="noopener noreferrer" className="w-7 h-7 flex items-center justify-center text-black hover:opacity-50 transition-opacity cursor-pointer">
            <i className="ri-facebook-line text-lg"></i>
          </a>
          <button className="text-black cursor-pointer" onClick={() => setMenuOpen(!menuOpen)}>
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`}></i>
          </button>
        </div>

        {/* Right nav */}
        <nav className="hidden md:flex items-center gap-6">
          {/* Build For Me button + dropdown */}
          <div className="relative">
            <button
              onClick={() => setBuildOpen(!buildOpen)}
              className="text-sm font-semibold text-white bg-black px-4 py-1.5 rounded-full hover:bg-black/80 transition-colors flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
            >
              Build For Me
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${buildOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {buildOpen && <BuildForMeDropdown onClose={() => setBuildOpen(false)} />}
          </div>
        </nav>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-black/10 px-5 py-4 flex flex-col gap-4">
          <Link to="/" className="text-sm text-black" onClick={() => setMenuOpen(false)}>Index</Link>
          <Link to="/brutal-about" className="text-sm text-black" onClick={() => setMenuOpen(false)}>About</Link>
          <button className="text-sm text-black text-left">Search</button>
          <a href="tel:+19512909947" className="text-sm text-black font-semibold">Call Us — +1 (951) 290-9947</a>
          <button className="text-sm text-black text-left font-semibold" onClick={() => { setMenuOpen(false); setBuildOpen(true); }}>Submit a Request</button>
          {/* Social links */}
          <div className="flex items-center gap-4 pt-1 border-t border-black/10">
            <a href="https://www.instagram.com/novaaidigitalllc/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-black hover:opacity-50 transition-opacity cursor-pointer">
              <i className="ri-instagram-line text-base"></i>
              Instagram
            </a>
            <a href="https://www.facebook.com/profile.php?id=61580586208643" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-black hover:opacity-50 transition-opacity cursor-pointer">
              <i className="ri-facebook-line text-base"></i>
              Facebook
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
